<template>
  <div class="d-flex flex-column-fluid credit-line-approval-page">
    <div class="container">
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Approve credit line</h1>
            <p class="subtitle">Review facility details and approve this credit line</p>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading credit line...</div>
      </div>

      <div v-else-if="error" class="result-card error-card">
        <i class="las la-exclamation-circle result-icon"></i>
        <h2 class="result-title">Unable to load credit line</h2>
        <p class="result-message">{{ error }}</p>
      </div>

      <div v-else-if="creditLine" class="approval-content">
        <div class="facility-card">
          <div class="card-header">
            <h5 class="card-title">Facility details</h5>
            <span :class="['status-badge', statusBadgeClass]">{{ statusLabel }}</span>
          </div>
          <div class="card-body">
            <div class="details-grid">
              <div class="detail-item">
                <label>Business</label>
                <div class="value">{{ businessName }}</div>
              </div>
              <div class="detail-item">
                <label>Limit</label>
                <div class="value">{{ formatMoney(creditLine.limit) }}</div>
              </div>
              <div class="detail-item">
                <label>Settlement fee</label>
                <div class="value">{{ formatMoney(creditLine.settlement_fee) }}</div>
              </div>
              <div class="detail-item">
                <label>Onboarding fee</label>
                <div class="value">{{ formatMoney(creditLine.onboarding_fee) }}</div>
              </div>
              <div class="detail-item">
                <label>Currency</label>
                <div class="value">{{ creditLine.currency || '—' }}</div>
              </div>
              <div class="detail-item">
                <label>Billing contact</label>
                <div class="value pre-line">{{ billingContact }}</div>
              </div>
              <div class="detail-item">
                <label>Account holder</label>
                <div class="value">{{ bankDetails.account_holder_name || '—' }}</div>
              </div>
              <div class="detail-item">
                <label>IBAN</label>
                <div class="value">{{ bankDetails.account_iban || '—' }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="actionError" class="alert alert-danger action-error">
          <i class="material-icons">error</i>
          {{ actionError }}
        </div>

        <div v-if="alreadyApprovedNotice" class="alert alert-info action-error">
          <i class="las la-info-circle"></i>
          Already approved
        </div>

        <button
          v-if="isAwaitingApproval"
          type="button"
          class="btn btn-approve"
          :disabled="approving"
          @click="approveCreditLine"
        >
          <span v-if="approving" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="las la-check-circle"></i>
          {{ approving ? 'Approving...' : 'Approve credit line' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import CompaniesService from "@/services/companies.service";
import { formatErrorMessage } from "@/utils/random_utils";

const APPROVED_TOAST = "Credit line approved. Onboarding invoice will send if enabled.";

export default {
  name: "CreditLineApproval",
  data() {
    return {
      loading: true,
      approving: false,
      error: null,
      actionError: null,
      alreadyApprovedNotice: false,
      creditLine: null,
      businessName: "—"
    };
  },
  computed: {
    creditLineStatus() {
      return FinancingService.normalizeCreditLineStatus(
        this.creditLine && this.creditLine.status
      );
    },
    isAwaitingApproval() {
      return this.creditLineStatus === "AWAITING_APPROVAL";
    },
    isApproved() {
      return this.creditLineStatus === "APPROVED";
    },
    statusLabel() {
      if (this.isAwaitingApproval) return "Awaiting approval";
      if (this.isApproved) return "Approved";
      return this.creditLineStatus || "—";
    },
    statusBadgeClass() {
      if (this.isAwaitingApproval) return "status-awaiting";
      if (this.isApproved) return "status-approved";
      return "status-default";
    },
    billingContact() {
      if (!this.creditLine || !this.creditLine.billing_contact) return "—";
      return this.creditLine.billing_contact;
    },
    bankDetails() {
      const details = this.creditLine && this.creditLine.bank_details;
      if (!details) return {};
      if (typeof details === "string") {
        try {
          return JSON.parse(details) || {};
        } catch (e) {
          return {};
        }
      }
      return details;
    }
  },
  mounted() {
    this.loadCreditLine();
  },
  methods: {
    async loadCreditLine() {
      const businessId = this.$route.params.businessId;
      const creditLineId = this.$route.params.creditLineId;
      this.loading = true;
      this.error = null;
      this.actionError = null;
      this.alreadyApprovedNotice = false;
      this.creditLine = null;

      if (!businessId || !creditLineId) {
        this.error = "Credit line not found";
        this.loading = false;
        return;
      }

      try {
        const [financingResponse, businessResponse] = await Promise.all([
          FinancingService.getFinancingForBusiness(businessId),
          CompaniesService.get(businessId, { list_view: true }).catch(() => null)
        ]);

        const creditLine = FinancingService.unwrapEntity(financingResponse);
        if (!creditLine || !creditLine.id) {
          this.error = "Credit line not found";
          return;
        }

        if (String(creditLine.id) !== String(creditLineId)) {
          this.error = "This approval link does not match the credit line for this business.";
          return;
        }

        this.creditLine = creditLine;
        const business = FinancingService.unwrapEntity(businessResponse) || (businessResponse && businessResponse.data);
        this.businessName = (business && business.name) || (creditLine.business_name) || "—";
      } catch (error) {
        this.error = this.resolveLoadError(error);
      } finally {
        this.loading = false;
      }
    },

    async approveCreditLine() {
      if (this.approving || !this.isAwaitingApproval || !this.creditLine) return;

      this.approving = true;
      this.actionError = null;
      this.alreadyApprovedNotice = false;

      try {
        const response = await FinancingService.approveCreditLine(this.creditLine.id);
        const updated = FinancingService.unwrapEntity(response) || this.creditLine;
        this.creditLine = Object.assign({}, this.creditLine, updated, {
          status: FinancingService.normalizeCreditLineStatus((updated && updated.status) || "APPROVED")
        });
        this.$showAlert(APPROVED_TOAST, "success");
      } catch (error) {
        const refreshed = await this.refreshApprovedState();
        if (refreshed || this.isAlreadyApprovedError(error)) {
          this.alreadyApprovedNotice = true;
          this.$showAlert("Already approved", "info");
          return;
        }
        this.actionError = this.resolveApproveError(error);
      } finally {
        this.approving = false;
      }
    },

    async refreshApprovedState() {
      const businessId = this.$route.params.businessId;
      if (!businessId) return false;
      try {
        const financingResponse = await FinancingService.getFinancingForBusiness(businessId);
        const creditLine = FinancingService.unwrapEntity(financingResponse);
        if (!creditLine) return false;
        this.creditLine = Object.assign({}, this.creditLine || {}, creditLine);
        return FinancingService.normalizeCreditLineStatus(creditLine.status) === "APPROVED";
      } catch (e) {
        return false;
      }
    },

    formatMoney(amount) {
      const currency = (this.creditLine && this.creditLine.currency) || "";
      if (amount === undefined || amount === null || amount === "") return "—";
      const number = Number(amount);
      if (isNaN(number)) return String(amount);
      const formatted = number.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return currency ? `${formatted} ${currency}` : formatted;
    },

    getErrorText(error) {
      if (!error) return "";
      if (typeof error === "string") return error;
      if (error.message) return String(error.message);
      return formatErrorMessage(error) || "";
    },

    getErrorStatus(error) {
      if (!error) return 0;
      if (error.status) return error.status;
      if (error.response && error.response.status) return error.response.status;
      return 0;
    },

    isAlreadyApprovedError(error) {
      const text = this.getErrorText(error).toLowerCase();
      return text.indexOf("already approved") !== -1;
    },

    resolveLoadError(error) {
      const status = this.getErrorStatus(error);
      if (status === 404 || status === 400) return "Credit line not found";
      return formatErrorMessage(error) || "Failed to load this credit line. Please try again.";
    },

    resolveApproveError(error) {
      const status = this.getErrorStatus(error);
      if (status === 404) return "Credit line not found";
      return formatErrorMessage(error) || "Failed to approve credit line. Please try again.";
    }
  }
};
</script>

<style scoped lang="scss">
@import './index.scss';

.credit-line-approval-page {
  .container {
    max-width: 760px;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.loading-text {
  margin-top: 0.75rem;
  color: #64748b;
  font-size: 0.9375rem;
}

.result-card,
.facility-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.result-card {
  padding: 3rem 2rem;
  text-align: center;
}

.result-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.error-card .result-icon {
  color: #dc2626;
}

.result-title {
  margin: 0 0 0.5rem;
  font-size: 1.375rem;
  font-weight: 700;
  color: #0f172a;
}

.result-message {
  margin: 0;
  color: #64748b;
  font-size: 0.9375rem;
}

.approval-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.facility-card {
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
  }

  .card-body {
    padding: 1.25rem;
  }
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;
}

.detail-item {
  label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #64748b;
  }

  .value {
    font-size: 0.9375rem;
    color: #0f172a;
    font-weight: 600;

    &.pre-line {
      white-space: pre-line;
      font-weight: 500;
    }
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.status-awaiting {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.status-approved {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-default {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.action-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

.btn-approve {
  align-self: flex-start;
  background: #111827;
  border: none;
  color: #fff;
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  &:hover:not(:disabled) {
    background: #1f2937;
    color: #fff;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  i {
    font-size: 1.1rem;
  }
}

@media (max-width: 640px) {
  .details-grid {
    grid-template-columns: 1fr;
  }

  .btn-approve {
    width: 100%;
    justify-content: center;
  }
}
</style>
